import Cookies from 'js-cookie';

// Token storage shared by the axios client, auth-session and impersonation.
// No app imports here: axios.ts loads this at module top level.

// A token that was stored from an undefined value comes back as the literal
// string "undefined"/"null" rather than absent, and `Bearer undefined` 401s
// every request forever. Treat those as no token so the refresh path runs.
export const readToken = (value: string | null | undefined) =>
  value && value !== 'undefined' && value !== 'null' ? value : undefined;

const storage = (): Storage | undefined => (typeof localStorage === 'undefined' ? undefined : localStorage);

export const getAccessToken = () =>
  readToken(Cookies.get('accessToken')) || readToken(storage()?.getItem('accessToken'));

export const getRefreshToken = () =>
  readToken(Cookies.get('refreshToken')) || readToken(storage()?.getItem('refreshToken'));

const isSecureCookie = () => typeof window !== 'undefined' && window.location.protocol === 'https:';

// Cookies without `expires` are session cookies, so localStorage is what
// survives a browser restart: both tokens go to both places, always.
export const storeJarvisTokens = (accessToken: string, refreshToken: string) => {
  Cookies.set('accessToken', accessToken, { secure: isSecureCookie(), sameSite: 'strict' });
  Cookies.set('refreshToken', refreshToken, { secure: isSecureCookie(), sameSite: 'strict' });
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

/** Claims the backend puts in a Jarvis access token (see GenerateTokens / GenerateImpersonationTokens). */
export type AccessTokenClaims = {
  user_id?: number;
  exp?: number;
  impersonated?: boolean;
  impersonator_id?: number;
  impersonation_session_id?: number;
  [claim: string]: unknown;
};

/**
 * The payload of a JWT, without verifying it (only the backend can). Returns
 * null for anything that is not a three-part token with a JSON object payload.
 */
export const decodeTokenClaims = (token: string | null | undefined): AccessTokenClaims | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    return claims && typeof claims === 'object' && !Array.isArray(claims) ? (claims as AccessTokenClaims) : null;
  } catch {
    return null;
  }
};

/** Claims of the access token this browser sends, read the same way axios reads it. */
export const readAccessTokenClaims = (): AccessTokenClaims | null => decodeTokenClaims(getAccessToken());

/**
 * Who a token speaks for: the user, and whether (and in which session) they
 * are impersonated. Two tokens with the same identity are one session; a
 * routine refresh keeps it.
 */
export const tokenIdentity = (claims: AccessTokenClaims | null): string =>
  claims
    ? [
        String(claims.user_id ?? ''),
        claims.impersonated === true ? 'imp' : 'self',
        String(claims.impersonation_session_id ?? ''),
      ].join('|')
    : 'none';
